import Page from 'flarum/common/components/Page';
import { extend, override } from 'flarum/common/extend';
import classList from 'flarum/common/utils/classList';
import listItems from 'flarum/common/helpers/listItems';

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import extractText from 'flarum/common/utils/extractText';

interface SelectOption {
  value: string;
  label: string;
  [key: string]: any; // 允许额外的自定义属性
}

interface CategoriesSelectAttrs {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  onchange?: (value: string) => void;
  className?: string;
  optionRender?: (option: SelectOption, info: { index: number }) => JSX.Element;
  labelRender?: (option: SelectOption) => JSX.Element;
  // 是否显示搜索框
  showSearch?: boolean;
  showRemoveFilter?: boolean;
  removeFilterLabel?: string;
  // 搜索过滤函数
  filterOption?: (inputValue: string, option: SelectOption) => boolean;
}

export default class CategoriesSelect extends Component<CategoriesSelectAttrs> {
  private isOpen: boolean = false;
  private searchValue: string = '';
  private filteredOptions: SelectOption[] = [];
  private originalOptions: SelectOption[] = [];

  oninit(vnode: any) {
    super.oninit(vnode);
    this.selectedValue = vnode.attrs.value || '';
    this.filteredOptions = vnode.attrs.options || [];
  }

  oncreate(vnode: any) {
    super.oncreate && super.oncreate(vnode);
    // Add event listener for outside clicks
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  onremove() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.handleOutsideClick);
  }

  view(vnode: any) {
    const { options, placeholder, className, optionRender, labelRender, showSearch, filterOption, showRemoveFilter, removeFilterLabel } = vnode.attrs;
    const selectedOption = options.find((opt: SelectOption) => opt.value === this.selectedValue);

    // 更新过滤选项
    this.updateFilteredOptions(vnode.attrs);

    return (
      <div
        className={classList({
          'custom-select': true,
          'is-open': this.isOpen,
          [className || '']: !!className,
        })}
      >
        {/* 输入框显示区域 */}
        <div className="select-display select-input" onclick={() => this.toggleDropdown()}>
          <div
            className={classList({
              'select-content': true,
              'is-placeholder': !selectedOption,
            })}
          >
            {selectedOption ? (labelRender ? labelRender(selectedOption) : selectedOption.label) : placeholder || ''}
          </div>
          <i
            className={classList({
              'select-arrow': true,
              'is-rotated': this.isOpen,
              'fas fa-chevron-down': true,
            })}
          ></i>
        </div>

        {/* 下拉选项列表 */}
        {this.isOpen && (
          <div className="select-dropdown">
            {/* Remove Filter 功能 */}
            {showRemoveFilter && this.selectedValue && (
              <>
                <div className="select-remove-filter" onclick={() => this.removeFilter(vnode)}>
                  {removeFilterLabel || ''}
                </div>
                <div className="remove-filter-line" />
              </>
            )}

            {/* 搜索框 */}
            {showSearch && (
              <div className="select-search">
                <input
                  type="text"
                  className="select-search-input"
                  placeholder="搜索选项..."
                  value={this.searchValue}
                  oninput={(e: Event) => this.handleSearch(e, vnode)}
                  onclick={(e: Event) => e.stopPropagation()}
                />
              </div>
            )}

            {/* 选项列表 */}
            <div className="select-options">
              {this.filteredOptions.length > 0 ? (
                this.filteredOptions.map((option: SelectOption, index: number) => (
                  <div
                    key={option.value}
                    className={classList({
                      'select-option': true,
                      'is-selected': option.value === this.selectedValue,
                    })}
                    onclick={() => this.selectOption(option, vnode)}
                  >
                    {optionRender ? optionRender(option, { index }) : option.label}
                  </div>
                ))
              ) : (
                <div className="select-no-data">暂无数据</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  private toggleDropdown() {
    this.isOpen = !this.isOpen;
    m.redraw();
  }

  private handleOutsideClick(event: Event) {
    const target = event.target as Element;
    const selectElement = target.closest('.custom-select');

    // 检查点击的元素是否属于当前组件实例
    const currentComponent = this.element;
    const isClickInsideCurrentComponent = currentComponent && currentComponent.contains(target);

    if (this.isOpen && !isClickInsideCurrentComponent) {
      this.isOpen = false;
      m.redraw();
    }
  }

  private removeFilter(vnode: any) {
    // 清除当前选择项
    this.selectedValue = '';
    this.isOpen = false;

    // 通知父组件选择项已清除
    if (vnode.attrs.onchange) {
      vnode.attrs.onchange('removeFilter');
    }

    m.redraw();
  }

  private updateFilteredOptions(attrs: CategoriesSelectAttrs) {
    const { options, filterOption } = attrs;

    // 保存原始选项列表
    if (options && options.length > 0) {
      this.originalOptions = options;
    }

    if (!this.searchValue) {
      this.filteredOptions = options || [];
      return;
    }

    if (filterOption) {
      this.filteredOptions = (options || []).filter((option) => filterOption(this.searchValue, option));
    } else {
      // 默认过滤逻辑：搜索 label
      this.filteredOptions = (options || []).filter((option) => option.label.toLowerCase().includes(this.searchValue.toLowerCase()));
    }
  }

  private handleSearch(event: Event, vnode: any) {
    const target = event.target as HTMLInputElement;
    this.searchValue = target.value;
    this.updateFilteredOptions(vnode.attrs);
    m.redraw();
  }

  private selectOption(option: SelectOption, vnode: any) {
    this.selectedValue = option.value;
    this.isOpen = false;
    this.searchValue = ''; // 选择后清空搜索

    if (vnode.attrs.onchange) {
      vnode.attrs.onchange(option.value);
    }

    // 触发重新渲染
    m.redraw();
  }
}
